'use client'

import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '@/hooks/use-auth'
import type {
  CallFlow,
  CreateCallFlowDTO,
  UpdateCallFlowDTO,
} from '@/lib/pbx/flow/types'

export function useCallFlows() {
  const [flows, setFlows] = useState<CallFlow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth, isAuthenticated } = useAuth()

  const loadFlows = useCallback(async () => {
    if (!isAuthenticated) return

    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth('/api/pbx/flows')
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to fetch flows')
      }
      const data = await res.json()
      setFlows(data.flows || [])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, isAuthenticated])

  useEffect(() => {
    loadFlows()
  }, [loadFlows])

  return {
    flows,
    isLoading,
    error,
    refetch: loadFlows,
  }
}

export function useCallFlow(id: string | null) {
  const [flow, setFlow] = useState<CallFlow | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth, isAuthenticated } = useAuth()

  const loadFlow = useCallback(async () => {
    if (!isAuthenticated || !id) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/flows/${id}`)
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to fetch flow')
      }
      const data = await res.json()
      setFlow(data.flow || null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, isAuthenticated, id])

  useEffect(() => {
    loadFlow()
  }, [loadFlow])

  return {
    flow,
    isLoading,
    error,
    refetch: loadFlow,
  }
}

export function useCallFlowMutations() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { fetchWithAuth } = useAuth()

  const createFlow = useCallback(async (dto: CreateCallFlowDTO) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth('/api/pbx/flows', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to create flow')
      }

      const data = await res.json()
      return data.flow as CallFlow
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  const updateFlow = useCallback(async (id: string, dto: UpdateCallFlowDTO): Promise<{
    flow: CallFlow;
    synced?: boolean;
    syncError?: string;
    syncWarning?: string;
  }> => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/flows/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto),
      })

      const data = await res.json()

      if (!res.ok) {
        console.error('[useCallFlowMutations.updateFlow] API error:', data.error)
        throw new Error(data.error || 'Failed to update flow')
      }
      return {
        flow: data.flow as CallFlow,
        synced: data.synced,
        syncError: data.syncError,
        syncWarning: data.syncWarning,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  const deleteFlow = useCallback(async (id: string, force = false) => {
    setIsLoading(true)
    setError(null)

    try {
      const url = force ? `/api/pbx/flows/${id}?force=true` : `/api/pbx/flows/${id}`
      const res = await fetchWithAuth(url, {
        method: 'DELETE',
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete flow')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  const syncFlow = useCallback(async (id: string): Promise<{
    success: boolean;
    synced: boolean;
    warning?: string;
    message?: string;
  }> => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/flows/${id}/sync`, {
        method: 'POST',
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || 'Failed to sync flow')
      }

      return {
        success: data.success,
        synced: data.synced ?? false,
        warning: data.warning,
        message: data.message,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  const validateFlow = useCallback(async (id: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await fetchWithAuth(`/api/pbx/flows/${id}/validate`, {
        method: 'POST',
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to validate flow')
      }

      return await res.json()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  return {
    createFlow,
    updateFlow,
    deleteFlow,
    syncFlow,
    validateFlow,
    isLoading,
    error,
  }
}
